import React from 'react';
import { PANEL_BODY_FILL_CLASSNAME } from '../constants/layout';
import { useLocale } from '../contexts/LocaleContext';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import { AddablePanelEmptyState, PanelEmptyState } from './PanelEmptyState';
import { NotesEmptyIllustration, NotesPwaShortcutIllustration } from './empty-illustration';

const loadingStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '3rem',
  color: '#9ca3af',
};

export const notesListContainerClassName = `${PANEL_BODY_FILL_CLASSNAME} w-full gap-4`;

export const notesSelectionColumnClassName = 'flex w-8 shrink-0 items-center justify-center';

export const NotesLoadingState: React.FC = () => {
  const { t } = useLocale();
  return (
    <div style={loadingStyle}>
      <div>{t('loading')}</div>
    </div>
  );
};

export const NotesPwaShortcutHint: React.FC = () => {
  const { t } = useLocale();
  const { canInstall, isInstalled, promptInstall } = useInstallPrompt();
  const [before, after] = t('notes_pwa_shortcut_hint').split('{{link}}');
  const installLabel = t('notes_pwa_shortcut_hint_install');
  const canPromptInstall = canInstall && !isInstalled;

  return (
    <div className="flex w-full flex-col items-center px-6 pt-8 text-center">
      <NotesPwaShortcutIllustration />
      <p className="max-w-sm text-xs text-muted-foreground">
        {isInstalled ? (
          t('notes_pwa_shortcut_hint_installed')
        ) : (
          <>
            {before}
            {canPromptInstall ? (
              <button
                type="button"
                className="font-medium text-primary underline underline-offset-2 hover:text-primary/80 hover:underline"
                onClick={() => {
                  void promptInstall();
                }}
              >
                {installLabel}
              </button>
            ) : (
              installLabel
            )}
            {after ?? ''}
          </>
        )}
      </p>
    </div>
  );
};

interface NotesEmptyStateProps {
  canEdit?: boolean;
  onAdd?: () => void;
}

export const NotesEmptyState: React.FC<NotesEmptyStateProps> = ({ canEdit = false, onAdd }) => {
  const { t } = useLocale();
  const shortcutHint = <NotesPwaShortcutHint />;

  if (canEdit && onAdd) {
    return (
      <AddablePanelEmptyState
        illustration={<NotesEmptyIllustration />}
        titleKey="notes_empty_title"
        descriptionKey="notes_empty_description"
        addLabelKey="create_note"
        canEdit={canEdit}
        onAdd={onAdd}
        footer={shortcutHint}
      />
    );
  }

  return (
    <PanelEmptyState
      illustration={<NotesEmptyIllustration />}
      title={t('notes_empty_title')}
      description={t('notes_empty_description')}
      footer={shortcutHint}
    />
  );
};
